import type {
  ColumnOrderColumnEnum,
  UISandboxMenuSection as UISandboxMenuSectionTypes,
} from '@creoate/virgo-content-api';

// mui
import Grid2 from '@mui/material/Grid2';

// hooks
import useVirgoContent from 'hooks/useVirgoContent';

// components
import CategoryLink from './CategoryLink';
import BannersImage from '../components/BannersImage';
import MenuTitle from '../components/MenuTitle';

// utils
import { TopMenuPopovers } from '../utils/types';

const UISandboxMenuSection = ({ hoveredNavLinkItem }: TopMenuPopovers) => {
  const { topMenu } = useVirgoContent();

  const selectedMenu = topMenu?.filter(
    (menu, index) => index === hoveredNavLinkItem?.index && menu?.component === hoveredNavLinkItem?.component
  )?.[0] as UISandboxMenuSectionTypes;

  return (
    <Grid2
      container
      width="100%"
      spacing="20px"
      sx={{ overflow: 'hidden' }}
      key={`ui.sandbox-menu-section-${hoveredNavLinkItem?.index}`}
      data-testid={`desktop-top-menu-content-${hoveredNavLinkItem?.component}-${hoveredNavLinkItem?.label}`}
    >
      {selectedMenu?.columnsOrder?.map((item) => {
        if ((item.column as `${ColumnOrderColumnEnum}`) === 'Categories') {
          const { title } = selectedMenu.categoriesColumn;
          return (
            <Grid2
              size={{ xs: 12, md: 3 }}
              sx={{
                height: '100%',
                overflowY: 'auto',
                '&::-webkit-scrollbar': {
                  width: '7px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#9E9E9E',
                  borderRadius: '4px',
                },
              }}
              key={`ui.sandbox-menu-section-${hoveredNavLinkItem?.index}-categories`}
            >
              {title && <MenuTitle title={title} />}

              {selectedMenu?.categoriesColumn?.categories?.map((category, index) => (
                <CategoryLink
                  label={category?.name}
                  href={`/product-category/${category?.slug}`}
                  key={`ui.sandbox-menu-section-${hoveredNavLinkItem?.index}-category-${index + 1}`}
                />
              ))}

              {selectedMenu?.categoriesColumn?.collections?.map((collection, index) => (
                <CategoryLink
                  label={collection?.name}
                  href={`/collections/${collection?.slug}`}
                  key={`ui.sandbox-menu-section-${hoveredNavLinkItem?.index}-collection-${index + 1}`}
                />
              ))}

              {selectedMenu?.categoriesColumn?.links?.map((link, index) => (
                <CategoryLink
                  href={link?.url}
                  label={link?.label}
                  key={`ui.sandbox-menu-section-${hoveredNavLinkItem?.index}-link-${index + 1}`}
                />
              ))}
            </Grid2>
          );
        }

        if ((item.column as `${ColumnOrderColumnEnum}`) === 'Products') {
          const { title } = selectedMenu.productsColumn;
          return (
            <Grid2
              size={{ xs: 12, md: 3 }}
              sx={{
                height: '100%',
                overflowY: 'auto',
                '&::-webkit-scrollbar': {
                  width: '7px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#9E9E9E',
                  borderRadius: '4px',
                },
              }}
              key={`ui.sandbox-menu-section-${hoveredNavLinkItem?.index}-products`}
            >
              {title && <MenuTitle title={title} />}

              {selectedMenu?.productsColumn?.products?.map((product, index) => (
                <CategoryLink
                  label={product?.name}
                  href={`/product/${product?.slug}`}
                  key={`ui.sandbox-menu-section-${hoveredNavLinkItem?.index}-product-${index + 1}`}
                />
              ))}

              {selectedMenu?.productsColumn?.links?.map((link, index) => (
                <CategoryLink
                  href={link?.url}
                  label={link?.label}
                  key={`ui.sandbox-menu-section-${hoveredNavLinkItem?.index}-product-link-${index + 1}`}
                />
              ))}
            </Grid2>
          );
        }

        if ((item.column as `${ColumnOrderColumnEnum}`) === 'Wholesalers') {
          const { title } = selectedMenu.wholesalersColumn;
          return (
            <Grid2
              size={{ xs: 12, md: 3 }}
              sx={{
                height: '100%',
                overflowY: 'auto',
                '&::-webkit-scrollbar': {
                  width: '7px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#9E9E9E',
                  borderRadius: '4px',
                },
              }}
              key={`ui.sandbox-menu-section-${hoveredNavLinkItem?.index}-wholesalers`}
            >
              {title && <MenuTitle title={title} />}

              {selectedMenu?.wholesalersColumn?.wholesalers?.map((wholesaler, index) => (
                <CategoryLink
                  label={wholesaler?.brandName}
                  href={`/store/${wholesaler?.slug}`}
                  key={`ui.sandbox-menu-section-${hoveredNavLinkItem?.index}-wholesaler-${index + 1}`}
                />
              ))}

              {selectedMenu?.wholesalersColumn?.links?.map((link, index) => (
                <CategoryLink
                  href={link.url}
                  label={link?.label}
                  key={`ui.sandbox-menu-section-${hoveredNavLinkItem?.index}-wholesaler-link-${index + 1}`}
                />
              ))}
            </Grid2>
          );
        }

        if ((item.column as `${ColumnOrderColumnEnum}`) === 'Banners') {
          return (
            <Grid2
              spacing="8px"
              direction="column"
              alignItems="start"
              justifyContent="start"
              size={{ xs: 12, md: 3 }}
              sx={{ height: '100%', overflowY: 'auto', padding: '8px 0' }}
              key={`ui.sandbox-menu-section-${hoveredNavLinkItem?.index}-banners`}
            >
              {selectedMenu?.bannersColumn?.banner?.desktop?.map((bannerImage) => (
                <BannersImage
                  alt={bannerImage?.link}
                  href={bannerImage?.link}
                  src={bannerImage?.image?.url}
                  key={`ui.sandbox-menu-section-${hoveredNavLinkItem?.index}-banner`}
                  component={
                    hoveredNavLinkItem?.label?.toLowerCase() === 'trending' ? 'menuSection' : 'menuSandboxSection'
                  }
                />
              ))}
            </Grid2>
          );
        }

        return null;
      })}
    </Grid2>
  );
};

export default UISandboxMenuSection;
